/* ResetPassword — landing for the reset link emailed by POST /auth/forgot-password.
   URL: /reset-password?token=… */
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Lock, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { apiResetPassword, isStrongPassword, PASSWORD_HINT, formatApiErrorDetail } from "@/lib/auth";

export default function ResetPassword() {
    const [params] = useSearchParams();
    const nav = useNavigate();
    const token = params.get("token") || "";
    const [password, setPassword] = useState("");
    const [confirmPw, setConfirmPw] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!token) { toast.error("Missing token — request a new link"); return; }
        if (!isStrongPassword(password)) { toast.error(PASSWORD_HINT); return; }
        if (password !== confirmPw) { toast.error("Passwords don't match"); return; }
        try {
            setBusy(true);
            await apiResetPassword({ token, password });
            toast.success("Password updated — signing you in…");
            nav("/member", { replace: true });
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Reset failed");
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-cafe-cream flex flex-col">
            <header className="px-4 sm:px-6 py-4 border-b border-border">
                <Link to="/" className="font-serif text-xl text-cafe-espresso hover:text-cafe-olive">
                    kaily<span className="text-cafe-terracotta">.</span>
                </Link>
            </header>
            <main className="flex-1 flex items-center justify-center px-4 py-12">
                <div className="w-full max-w-md">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-cafe-olive font-bold mb-2">Member Hub</p>
                    <h1 className="font-serif text-3xl sm:text-4xl text-cafe-espresso mb-2">
                        Pick a new password.
                    </h1>
                    <p className="text-sm text-cafe-mocha mb-8">{PASSWORD_HINT}</p>

                    <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
                        <label className="block">
                            <span className="text-[10px] uppercase tracking-[0.18em] text-cafe-mocha font-bold mb-1.5 flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> New password</span>
                            <input
                                type="password"
                                autoFocus
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full border border-border focus:border-cafe-olive rounded-xl px-3.5 py-2.5 text-sm bg-white outline-none"
                                data-testid="reset-password-input"
                            />
                        </label>
                        <label className="block">
                            <span className="text-[10px] uppercase tracking-[0.18em] text-cafe-mocha font-bold mb-1.5 flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> Confirm</span>
                            <input
                                type="password"
                                value={confirmPw}
                                onChange={(e) => setConfirmPw(e.target.value)}
                                className="w-full border border-border focus:border-cafe-olive rounded-xl px-3.5 py-2.5 text-sm bg-white outline-none"
                                data-testid="reset-confirm-input"
                            />
                        </label>
                        <button
                            type="submit"
                            disabled={busy}
                            className="w-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream rounded-full px-6 py-3.5 text-sm font-bold uppercase tracking-[0.14em] flex items-center justify-center gap-2 disabled:opacity-50"
                            data-testid="reset-submit-btn"
                        >
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Update password <ArrowRight className="h-4 w-4" /></>}
                        </button>
                    </form>
                </div>
            </main>
        </div>
    );
}
